/**
 * Gör Car.info-specifikationer visningsbara: underattribut tas med, identiteten är id.
 */

export interface DisplayAttr {
  id: string;
  name: string;
  values?: unknown;
  unit?: string;
}

interface SpecNode {
  id?: string | number;
  name?: string;
  unit?: string;
  values?: unknown;
  attributes?: unknown;
  children?: unknown;
}

function childNodes(node: SpecNode): SpecNode[] {
  const raw = node.attributes ?? node.children;
  if (Array.isArray(raw)) return raw.filter((item) => item && typeof item === 'object') as SpecNode[];
  if (raw && typeof raw === 'object') return Object.values(raw as Record<string, SpecNode>);
  return [];
}

function hasValues(values: unknown): boolean {
  if (Array.isArray(values)) return values.some((value) => value != null && String(value).trim() !== '');
  return values != null && String(values).trim() !== '';
}

/** Tar med förälder som har eget värde och alla underattribut. Namnet är visningstext. */
export function flattenSpecAttributes(attributes: Record<string, SpecNode> | null | undefined): DisplayAttr[] {
  const out: DisplayAttr[] = [];
  const seen = new WeakSet<object>();

  const visit = (attr: SpecNode, parentId?: string, parentName?: string) => {
    if (seen.has(attr)) return;
    seen.add(attr);
    const ownName = typeof attr.name === 'string' ? attr.name.trim() : '';
    const ownId = attr.id != null ? String(attr.id) : '';
    const key = ownId ? (parentId ? `${parentId}/${ownId}` : ownId) : `${parentId ?? 'attr'}_${out.length}`;
    if (hasValues(attr.values) && ownName) {
      out.push({
        id: key,
        name: parentName ? `${parentName}: ${ownName}` : ownName,
        values: attr.values,
        unit: attr.unit,
      });
    }
    const nextParent = ownName || parentName;
    const nextId = ownId || parentId;
    for (const child of childNodes(attr)) visit(child, nextId, nextParent);
  };

  if (!attributes || typeof attributes !== 'object') return out;
  for (const value of Object.values(attributes)) {
    if (value && typeof value === 'object') visit(value);
  }
  return out;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** Plockar ut löv ur paket, däck och varningar så de kan visas som rader. */
export function jsonLeavesToAttrs(value: unknown, idPrefix: string): DisplayAttr[] {
  const out: DisplayAttr[] = [];

  const walk = (node: unknown, idPath: string, labelPath: string) => {
    if (node == null || node === '') return;
    if (isPrimitive(node)) {
      out.push({ id: idPath, name: labelPath || idPath, values: [node] });
      return;
    }
    if (Array.isArray(node)) {
      if (node.every((item) => item == null || item === '' || isPrimitive(item))) {
        const values = node.filter((item) => item != null && item !== '');
        if (values.length > 0) out.push({ id: idPath, name: labelPath || idPath, values });
        return;
      }
      node.forEach((item, index) => {
        const n = index + 1;
        walk(item, `${idPath}_${n}`, labelPath ? `${labelPath} ${n}` : String(n));
      });
      return;
    }
    if (typeof node === 'object') {
      for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
        walk(child, `${idPath}_${key}`, labelPath ? `${labelPath} · ${key}` : key);
      }
    }
  };

  walk(value, idPrefix, '');
  return out;
}
